import { create } from "zustand";
import { api, ApiError, apiErrorMessage } from "@/api/client";
import type { Paged } from "@/api/types";
import { useHive } from "@/store/useHive";
import type {
  HiveWebhook,
  WebhookDelivery,
  WebhookDirection,
  WebhookEventDef,
  WebhookReveal,
  WebhookScope,
} from "@/types";

type DataStatus = "idle" | "loading" | "ready" | "error";

export interface WebhookCreateInput {
  direction: WebhookDirection;
  name: string;
  description?: string;
  scope: WebhookScope;
  workspaceId?: string;
  integrationId?: string;
  url?: string;
  events?: string[];
}

export interface WebhookPatchInput {
  name?: string;
  description?: string;
  scope?: WebhookScope;
  workspaceId?: string | null;
  integrationId?: string | null;
  url?: string;
  events?: string[];
  active?: boolean;
}

interface TestOutcome {
  ok: boolean;
  deliveryId: string;
  statusCode?: number;
  durationMs?: number;
  detail: string;
}

interface WebhookState {
  webhooks: HiveWebhook[];
  status: DataStatus;
  eventDefs: WebhookEventDef[];
  selectedId: string | null;
  /** Delivery log per webhook id (latest page). */
  deliveries: Record<string, WebhookDelivery[]>;
  deliveriesLoading: string | null;
  /** One-time secret reveal (create/rotate/reveal) — cleared on dismiss. */
  reveal: WebhookReveal | null;
  lastTest: (TestOutcome & { webhookId: string }) | null;

  hydrate: () => Promise<void>;
  loadEvents: () => Promise<void>;
  select: (id: string | null) => void;
  clearReveal: () => void;
  create: (input: WebhookCreateInput) => Promise<HiveWebhook | null>;
  update: (id: string, patch: WebhookPatchInput) => Promise<HiveWebhook | null>;
  remove: (id: string) => Promise<boolean>;
  rotateSecret: (id: string) => Promise<void>;
  revealUrl: (id: string) => Promise<void>;
  test: (id: string) => Promise<void>;
  loadDeliveries: (id: string) => Promise<void>;
  redeliver: (webhookId: string, deliveryId: string) => Promise<void>;
}

const toast = (t: { title: string; body?: string; tone: "info" | "success" | "warning" | "error" }) =>
  useHive.getState().pushToast(t);

const errBody = (err: unknown, fallback: string): string =>
  err instanceof ApiError ? apiErrorMessage(err) : fallback;

export const useWebhooks = create<WebhookState>((set, get) => {
  const replace = (id: string, updated: HiveWebhook) =>
    set((s) => ({ webhooks: s.webhooks.map((w) => (w.id === id ? updated : w)) }));

  return {
    webhooks: [],
    status: "idle",
    eventDefs: [],
    selectedId: null,
    deliveries: {},
    deliveriesLoading: null,
    reveal: null,
    lastTest: null,

    hydrate: async () => {
      if (get().status === "loading") return;
      set((s) => ({ status: s.status === "ready" ? "ready" : "loading" }));
      try {
        const res = await api.get<Paged<HiveWebhook>>("/api/v1/webhooks?limit=200");
        set({ webhooks: res.items, status: "ready" });
      } catch {
        set((s) => ({ status: s.status === "ready" ? "ready" : "error" }));
      }
    },

    loadEvents: async () => {
      if (get().eventDefs.length > 0) return;
      try {
        const res = await api.get<{ items: WebhookEventDef[] }>("/api/v1/webhooks/events");
        set({ eventDefs: res.items });
      } catch {
        /* the create form falls back to a free-text event list */
      }
    },

    select: (id) => set({ selectedId: id, lastTest: null }),
    clearReveal: () => set({ reveal: null }),

    create: async (input) => {
      try {
        const res = await api.post<{ webhook: HiveWebhook; secret: string; inboundUrl: string | null }>(
          "/api/v1/webhooks",
          input,
        );
        set((s) => ({
          webhooks: [res.webhook, ...s.webhooks],
          selectedId: res.webhook.id,
          reveal: { webhookId: res.webhook.id, secret: res.secret, inboundUrl: res.inboundUrl, reason: "created" },
        }));
        toast({ title: "Webhook created", body: `${res.webhook.name} is live`, tone: "success" });
        useHive.getState().pushActivity({
          kind: "integration",
          title: "Webhook created",
          detail: `${res.webhook.name} · ${res.webhook.direction}`,
          app: "integrations",
        });
        return res.webhook;
      } catch (err) {
        toast({ title: "Couldn't create webhook", body: errBody(err, "Unexpected error — retry"), tone: "error" });
        return null;
      }
    },

    update: async (id, patch) => {
      try {
        const updated = await api.patch<HiveWebhook>(`/api/v1/webhooks/${id}`, patch);
        replace(id, updated);
        return updated;
      } catch (err) {
        toast({ title: "Couldn't update webhook", body: errBody(err, "Change wasn't saved — retry"), tone: "error" });
        return null;
      }
    },

    remove: async (id) => {
      const hook = get().webhooks.find((w) => w.id === id);
      try {
        await api.del(`/api/v1/webhooks/${id}`);
        set((s) => ({
          webhooks: s.webhooks.filter((w) => w.id !== id),
          selectedId: s.selectedId === id ? null : s.selectedId,
        }));
        toast({ title: "Webhook deleted", body: hook ? `${hook.name} removed` : undefined, tone: "info" });
        return true;
      } catch (err) {
        toast({ title: "Couldn't delete webhook", body: errBody(err, "Retry"), tone: "error" });
        return false;
      }
    },

    rotateSecret: async (id) => {
      try {
        const res = await api.post<{ secret: string; inboundUrl: string | null }>(
          `/api/v1/webhooks/${id}/rotate-secret`,
        );
        set({ reveal: { webhookId: id, secret: res.secret, inboundUrl: res.inboundUrl, reason: "rotated" } });
        toast({ title: "Secret rotated", body: "Update every sender — the old secret stopped working", tone: "warning" });
      } catch (err) {
        toast({ title: "Couldn't rotate secret", body: errBody(err, "Retry"), tone: "error" });
      }
    },

    revealUrl: async (id) => {
      try {
        const res = await api.post<{ inboundUrl: string; secret: string }>(`/api/v1/webhooks/${id}/reveal`);
        set({ reveal: { webhookId: id, secret: res.secret, inboundUrl: res.inboundUrl, reason: "revealed" } });
      } catch (err) {
        toast({ title: "Couldn't reveal URL", body: errBody(err, "Retry"), tone: "error" });
      }
    },

    test: async (id) => {
      try {
        const res = await api.post<TestOutcome>(`/api/v1/webhooks/${id}/test`);
        set({ lastTest: { ...res, webhookId: id } });
        toast({
          title: res.ok ? "Test delivery sent" : "Test delivery failed",
          body: res.detail,
          tone: res.ok ? "success" : "error",
        });
        // Refresh the row (stats moved) and the visible log.
        void get().hydrate();
        void get().loadDeliveries(id);
      } catch (err) {
        toast({ title: "Test failed", body: errBody(err, "Could not reach the Hive API"), tone: "error" });
      }
    },

    loadDeliveries: async (id) => {
      set({ deliveriesLoading: id });
      try {
        const res = await api.get<Paged<WebhookDelivery>>(`/api/v1/webhooks/${id}/deliveries?limit=50`);
        set((s) => ({ deliveries: { ...s.deliveries, [id]: res.items }, deliveriesLoading: null }));
      } catch {
        set({ deliveriesLoading: null });
      }
    },

    redeliver: async (webhookId, deliveryId) => {
      try {
        const res = await api.post<TestOutcome>(
          `/api/v1/webhooks/${webhookId}/deliveries/${deliveryId}/redeliver`,
        );
        toast({
          title: res.ok ? "Redelivered" : "Redelivery failed",
          body: res.detail,
          tone: res.ok ? "success" : "error",
        });
        void get().loadDeliveries(webhookId);
        void get().hydrate();
      } catch (err) {
        toast({ title: "Redelivery failed", body: errBody(err, "Retry"), tone: "error" });
      }
    },
  };
});
